import {useState, useCallback, useRef, useEffect} from 'react'

import {TransactionResultType} from '../types/orderChangeResult.types'

/**
 * Generic polling hook for order request results.
 *
 * Polls at specified intervals until either:
 * - A result is returned from getUpdatedStatus
 * - Maximum retries are exceeded (returns 'pending')
 *
 * @param pollIntervalMs - Interval between poll attempts in milliseconds
 * @param maxRetries - Maximum number of polling attempts
 * @param getUpdatedStatus - Async function that returns the result or undefined to continue polling
 *
 * @example
 * ```tsx
 * const checkStatus = async () => {
 *   const data = await fetchStatus();
 *   if (data.status === 'completed') return 'success';
 *   if (data.status === 'failed') return 'failure';
 *   return undefined; // continue polling
 * };
 *
 * const { triggerPolling, isPolling, result } = useOrderRequestResultPolling(
 *   3000,
 *   10,
 *   checkStatus
 * );
 * ```
 */
export const useOrderRequestResultPolling = (
  pollIntervalMs: number,
  maxRetries: number,
  getUpdatedStatus: () => Promise<TransactionResultType | undefined>
): {
  triggerPolling: () => void
  isPolling: boolean
  result: TransactionResultType | undefined
} => {
  const [isPolling, setIsPolling] = useState(false)
  const [result, setResult] = useState<TransactionResultType | undefined>(undefined)
  const retriesRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollingRunRef = useRef(0)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      pollingRunRef.current += 1
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const poll = useCallback(
    async (pollingRun: number) => {
      try {
        const status = await getUpdatedStatus()
        if (pollingRun !== pollingRunRef.current) return

        if (status !== undefined) {
          // Got a final result
          setResult(status)
          setIsPolling(false)
          retriesRef.current = 0
          return
        }

        // Status still pending, check retries
        retriesRef.current += 1

        if (retriesRef.current >= maxRetries) {
          // Max retries exceeded, return pending
          setResult('pending')
          setIsPolling(false)
          retriesRef.current = 0
          return
        }

        // Schedule next poll
        timeoutRef.current = setTimeout(() => void poll(pollingRun), pollIntervalMs)
      } catch (error) {
        if (pollingRun !== pollingRunRef.current) return
        console.error('Polling error:', error)
        // On error, stop polling and return failure
        setResult('failure')
        setIsPolling(false)
        retriesRef.current = 0
      }
    },
    [getUpdatedStatus, maxRetries, pollIntervalMs]
  )

  const triggerPolling = useCallback(() => {
    const pollingRun = pollingRunRef.current + 1
    pollingRunRef.current = pollingRun

    // Reset state
    setResult(undefined)
    setIsPolling(true)
    retriesRef.current = 0

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Start polling
    void poll(pollingRun)
  }, [poll])

  return {
    triggerPolling,
    isPolling,
    result
  }
}
